import {
    Assignment,
    AssignmentStatus,
    FlashcardSet,
    FlashcardStudyState,
    ItemType,
    PracticeTestProgress,
    StudySession,
    StudySessionProgress,
    StudySessionProgressEnum,
    StudySessionType,
} from "@/graphql/schema";
import { DEFAULT_ASSIGNMENT_COMPLETION, DEFAULT_NOTE_COMPLETION, DEFAULT_STUDY_PROGRESS } from "./constants";
import { AssignmentSessionGroupedByUser, DEFAULT_ASSIGNMENT_RUBRIC_INPUT } from "../assignments/constants";
import { mean, sortBy, zip } from "lodash";
import { fromEntries } from "@/utils/genericUtils";

export const DEFAULT_STATS = {
    lastActive: 0,
    timeSpent: 0,
    cardsSeen: 0,
    rounds: null,
    testTaken: null,
    averageGrade: null,
    lastSubmission: null,
    testHistory: [],
    completed: undefined,
};

const values: { [key in StudySessionProgressEnum]: number } = {
    [StudySessionProgressEnum.NEW]: 0,
    [StudySessionProgressEnum.LEARNING]: 20,
    [StudySessionProgressEnum.REVIEWING]: 60,
    [StudySessionProgressEnum.MASTERED]: 100,
};

export const getProgressValue = (progress: StudySessionProgress): number => {
    const total = Object.values(progress).reduce((acc, curr) => acc + curr, 0);

    const keys = Object.keys(progress);
    return Math.floor(
        keys.reduce((acc, curr: StudySessionProgressEnum) => {
            const value = values[curr];
            return acc + (value * progress[curr]) / total;
        }, 0)
    );
};

export const getMasteredPercentage = (stats?: Record<StudySessionProgressEnum, number>) => {
    if (!stats) return 0;
    const totalTypes = Object.values(stats).reduce((acc, val) => acc + val, 0);
    const masteredProgress = totalTypes > 0 ? (stats.MASTERED / totalTypes) * 100 : 0;
    return masteredProgress.toFixed(0);
};

export const getProgressLabel = (progress: StudySessionProgressEnum): string => {
    switch (progress) {
        case StudySessionProgressEnum.NEW:
            return "Not started yet";
        case StudySessionProgressEnum.LEARNING:
            return "Still learning";
        case StudySessionProgressEnum.REVIEWING:
            return "Almost done";
        case StudySessionProgressEnum.MASTERED:
            return "Complete";
    }
};

export const getProgressBucket = (progressPercentage: number): StudySessionProgressEnum => {
    return progressPercentage > 90
        ? StudySessionProgressEnum.MASTERED
        : progressPercentage > 60
          ? StudySessionProgressEnum.REVIEWING
          : progressPercentage > 20
            ? StudySessionProgressEnum.LEARNING
            : StudySessionProgressEnum.NEW;
};

export type FlashcardStats = Record<string, Record<StudySessionProgressEnum, number>>;

export const calculateFlashcardStats = (dataObject: FlashcardsStudyAnalytics) => {
    return Object.values(dataObject).reduce((acc, userObj) => {
        for (const [flashcardId, { progress }] of Object.entries(userObj)) {
            if (!acc[flashcardId]) {
                acc[flashcardId] = DEFAULT_STUDY_PROGRESS;
            }
            acc[flashcardId][progress]++;
        }
        return acc;
    }, {} as FlashcardStats);
};

export type StudySessionCompletion = "STARTED" | "CONTINUED" | "COMPLETED";

export const getSessionCompletion = (progress?: StudySessionProgress): StudySessionCompletion => {
    if (!progress) return "STARTED";
    if (progress.NEW === 0 && progress.LEARNING === 0 && progress.REVIEWING === 0) {
        return "COMPLETED";
    } else if (progress.MASTERED === 0 && progress.LEARNING === 0 && progress.REVIEWING === 0) {
        return "STARTED";
    }
    return "CONTINUED";
};

export type TestHistory = {
    submitted: number;
    correct: number;
    wrong: number;
    skipped: number;
    percentage: number;
    timeSpent: number;
};

export type TestGeneralStats = {
    averageGrade: number;
    lastSubmission: number;
    timeSpent: number;
    testHistory: TestHistory[];
};

export type SetStudyAnalyticsForUser = {
    lastActive: number;
    timeSpent: number;
    cardsSeen: number;
    progressPercentage: number;
    statsPerMode: {
        [K in Exclude<StudySessionType, StudySessionType.TEST>]: {
            completed?: boolean;
            timeSpent: number;
            lastActive: number;
            cardsSeen: number;
        };
    } & {
        [StudySessionType.TEST]: TestGeneralStats;
    };
};

export type NoteStudyAnalyticsForUser = TestGeneralStats;

const getMostRecentStudySessionPerUser = ({
    studySessions,
    userIds,
}: {
    studySessions: StudySession[];
    userIds: string[];
}) =>
    studySessions
        ?.filter(({ userId }) => userIds.includes(userId))
        .reduce(
            (acc, curr) => {
                acc[curr.userId] ||= curr;
                if (acc[curr.userId].created < curr.created) acc[curr.userId] = curr;
                return acc;
            },
            {} as Record<string, StudySession>
        );

const getStudyProgressPerUser = ({ studySessions, userIds }: { studySessions: StudySession[]; userIds: string[] }) =>
    Object.entries(getMostRecentStudySessionPerUser({ studySessions, userIds })).map(
        ([userId, studySession]) => [userId, getProgressValue(studySession.progress)] as const
    );

export type UsersStudyAnalyticsPerItemType = {
    [ItemType.FLASHCARDSET]: Record<string, SetStudyAnalyticsForUser>;
    [ItemType.NOTE]: Record<string, NoteStudyAnalyticsForUser>;
    [ItemType.ASSIGNMENT]: Record<string, SetStudyAnalyticsForUser>;
};

type ItemTypesWithStudyAnalytics = ItemType.FLASHCARDSET | ItemType.NOTE | ItemType.ASSIGNMENT;

type GetUsersStudyAnalyticsProps<T extends ItemTypesWithStudyAnalytics> = {
    itemId: string | null | undefined;
    itemType: T;
    userIds?: string[];
} & (T extends ItemType.FLASHCARDSET
    ? {
          flashcardStudyStates: FlashcardStudyState[] | null | undefined;
      }
    : { flashcardStudyStates?: never }) &
    (T extends ItemType.ASSIGNMENT
        ? {
              assignmentSessionsByUser: AssignmentSessionGroupedByUser | null | undefined;
              assignment: Assignment | null | undefined;
              studySessions?: never;
          }
        : {
              assignmentSessionsByUser?: never;
              assignment?: never;
              studySessions: StudySession[] | null | undefined;
          });

type AssignmentStudyAnalyticsForUser = {
    grade: ResultGrade | null;
    submissions: number;
    lastSubmission: number;
    timeSpent: number;
};

type GetUsersStudyAnalyticsReturn<T extends ItemTypesWithStudyAnalytics> = T extends ItemType.FLASHCARDSET
    ? Record<string, SetStudyAnalyticsForUser> | null | undefined
    : T extends ItemType.NOTE
      ? Record<string, NoteStudyAnalyticsForUser> | null | undefined
      : Record<string, AssignmentStudyAnalyticsForUser> | null | undefined;

const getPracticeTestGeneralStats = (userId: string, studySessions: StudySession[]): TestGeneralStats | null => {
    const { practiceTests } = (studySessions || []).find(({ userId: _userId }) => _userId === userId) || {};
    const history = practiceTests.map(test => {
        const parsedScore = test.score.split("/").map(val => Number(val));
        return {
            submitted: test.timestamp,
            timeTaken: test.timeTaken,
            correct: parsedScore[0],
            wrong: parsedScore[1],
            skipped: parsedScore[2],
            percentage: (parsedScore[0] * 100) / (parsedScore[0] + parsedScore[1] + parsedScore[2]),
            timeSpent: test.timeTaken,
        };
    });

    return {
        averageGrade: history.reduce((acc, curr) => acc + curr.percentage, 0) / history.length,
        lastSubmission: history[history.length - 1]?.submitted || 0,
        testHistory: history,
        timeSpent: history.reduce((acc, curr) => acc + curr.timeTaken, 0),
    };
};

export const getUsersStudyAnalytics = <T extends ItemTypesWithStudyAnalytics>({
    studySessions,
    userIds,
    itemId,
    itemType,
    flashcardStudyStates,
    assignmentSessionsByUser,
    assignment,
}: GetUsersStudyAnalyticsProps<T>): GetUsersStudyAnalyticsReturn<T> => {
    if (!itemId) return null;

    if (
        (itemType !== ItemType.ASSIGNMENT && !studySessions) ||
        (itemType === ItemType.ASSIGNMENT && !assignmentSessionsByUser?.length && !assignment) ||
        (itemType === ItemType.FLASHCARDSET && !flashcardStudyStates?.length) ||
        !userIds
    )
        return undefined;

    const addUsersToResult = (result: GetUsersStudyAnalyticsReturn<T>) => {
        for (const userId of userIds) {
            result[userId] = {} as GetUsersStudyAnalyticsReturn<T>[string];
        }
    };

    switch (itemType) {
        case ItemType.FLASHCARDSET: {
            const result = {} as GetUsersStudyAnalyticsReturn<ItemType.FLASHCARDSET>;
            addUsersToResult(result as GetUsersStudyAnalyticsReturn<T>);

            for (const [userId, progressPercentage] of getStudyProgressPerUser({ studySessions, userIds })) {
                result[userId].progressPercentage = progressPercentage;
            }

            for (const flashcardStudyState of flashcardStudyStates?.filter(({ userId }) => userIds.includes(userId)) ||
                []) {
                const userId = flashcardStudyState.userId;
                result[userId] ||= {} as SetStudyAnalyticsForUser;

                result[userId].statsPerMode ||= {} as SetStudyAnalyticsForUser["statsPerMode"];
                for (const mode of Object.values(StudySessionType).filter(mode => mode !== StudySessionType.TEST)) {
                    result[flashcardStudyState.userId].statsPerMode[mode] ||=
                        {} as SetStudyAnalyticsForUser["statsPerMode"][typeof mode extends Exclude<
                            StudySessionType,
                            "TEST"
                        >
                            ? Exclude<StudySessionType, "TEST">
                            : StudySessionType.TEST];
                }

                result[userId].statsPerMode[StudySessionType.TEST] ||=
                    {} as SetStudyAnalyticsForUser["statsPerMode"][StudySessionType.TEST];

                const { practiceTests } = (studySessions || []).find(({ userId: _userId }) => _userId === userId) || {
                    practiceTests: [],
                };

                if (practiceTests?.length) {
                    const testGeneralStats = getPracticeTestGeneralStats(userId, studySessions);
                    result[userId].statsPerMode[StudySessionType.TEST].testHistory = testGeneralStats?.testHistory;
                    result[userId].statsPerMode[StudySessionType.TEST].averageGrade = testGeneralStats?.averageGrade;
                    result[userId].statsPerMode[StudySessionType.TEST].lastSubmission =
                        testGeneralStats?.lastSubmission;
                    result[userId].statsPerMode[StudySessionType.TEST].timeSpent = testGeneralStats?.timeSpent;
                }

                for (const { mode, timestamp, timeTaken } of flashcardStudyState.history) {
                    // time spent per mode
                    result[userId].statsPerMode[mode].timeSpent ||= 0;
                    result[userId].statsPerMode[mode].timeSpent += timeTaken;

                    // time spent overall
                    result[userId].timeSpent ||= 0;
                    result[userId].timeSpent += timeTaken;

                    if (mode !== StudySessionType.TEST) {
                        // last active per mode
                        result[userId].statsPerMode[mode].lastActive ||= timestamp;
                        if (result[userId].statsPerMode[mode].lastActive < timestamp) {
                            result[userId].statsPerMode[mode].lastActive = timestamp;
                        }

                        // last active overall
                        result[userId].lastActive ||= timestamp;
                        if (result[userId].lastActive < timestamp) {
                            result[userId].lastActive = timestamp;
                        }
                    }
                }

                // cards seen
                result[userId].cardsSeen ||= 0;
                if (flashcardStudyState.history.length > 0) {
                    result[userId].cardsSeen += 1;
                }

                // cards seen per mode
                for (const mode of Object.values(StudySessionType).filter(mode => mode !== StudySessionType.TEST)) {
                    result[userId].statsPerMode[mode].cardsSeen ||= 0;
                    if (flashcardStudyState.history.some(({ mode: _mode }) => _mode === mode)) {
                        result[userId].statsPerMode[mode].cardsSeen += 1;
                    }
                }

                // completed learn mode
                result[userId].statsPerMode[StudySessionType.LEARN].completed ||= false;
                if (flashcardStudyState.history.some(({ mode: _mode }) => _mode === StudySessionType.LEARN)) {
                    if (result[userId].progressPercentage === 100) {
                        result[userId].statsPerMode[StudySessionType.LEARN].completed = true;
                    }
                }
            }

            return result as GetUsersStudyAnalyticsReturn<T>;
        }
        case ItemType.NOTE: {
            const result = {} as GetUsersStudyAnalyticsReturn<ItemType.NOTE>;
            addUsersToResult(result as GetUsersStudyAnalyticsReturn<T>);

            for (const userId of userIds || []) {
                const { practiceTests } = (studySessions || []).find(({ userId: _userId }) => _userId === userId) || {
                    practiceTests: [],
                };

                if (practiceTests?.length) {
                    const testGeneralStats = getPracticeTestGeneralStats(userId, studySessions);
                    result[userId].testHistory = testGeneralStats?.testHistory;
                    result[userId].averageGrade = testGeneralStats?.averageGrade;
                    result[userId].lastSubmission = testGeneralStats?.lastSubmission;
                    result[userId].timeSpent = testGeneralStats?.timeSpent;
                }
            }

            return result as GetUsersStudyAnalyticsReturn<T>;
        }
        case ItemType.ASSIGNMENT: {
            const result = {} as GetUsersStudyAnalyticsReturn<ItemType.ASSIGNMENT>;
            addUsersToResult(result as GetUsersStudyAnalyticsReturn<T>);

            for (const userId of userIds || []) {
                const assignmentSessions = assignmentSessionsByUser?.[userId] || [];

                const mostRecentCompletedSession = assignmentSessions
                    .sort((a, b) => Number(b.submitted) - Number(a.submitted))
                    .find(({ status }) => status === AssignmentStatus.COMPLETED);

                result[userId].grade = mostRecentCompletedSession
                    ? getGradeFromScore({
                          score: mostRecentCompletedSession?.score || 0,
                          assignment,
                      })
                    : null;

                result[userId].submissions = assignmentSessions.length;
                result[userId].lastSubmission = Number(mostRecentCompletedSession?.submitted || 0);
                result[userId].timeSpent = assignmentSessions.reduce(
                    (acc, curr) => acc + Math.max(curr.duration ?? 0, 1),
                    0
                );
            }

            return result as GetUsersStudyAnalyticsReturn<T>;
        }
    }
};

export const getTestScore = (test: PracticeTestProgress) => {
    const scoreStats = test.score.split("/").map(Number);
    return scoreStats[0] / scoreStats.reduce((acc, curr) => acc + curr, 0);
};

export const getOverallNoteCompletion = ({
    studySessions,
    totalStudents,
}: {
    studySessions: StudySession[] | null | undefined;
    totalStudents: number;
}) => {
    if (!studySessions?.length) return DEFAULT_NOTE_COMPLETION;

    const testHistory = studySessions.flatMap(({ practiceTests }) => practiceTests).filter(Boolean);

    if (!testHistory.length) return DEFAULT_NOTE_COMPLETION;

    const testHistoryByUser = studySessions.reduce(
        (acc, studySession) => {
            const tests = studySession.practiceTests?.filter(Boolean);

            if (tests?.length) {
                acc[studySession.userId] = tests;
            }

            return acc;
        },
        {} as Record<string, PracticeTestProgress[]>
    );

    const averageScore = mean(testHistory.map(getTestScore));

    const averageTimeSpent = mean(testHistory.map(test => test.timeTaken));

    const averageIncreaseInScorePerRetest = mean(
        Object.keys(testHistoryByUser).map(userId => {
            const tests = sortBy(testHistoryByUser[userId], "timestamp");

            const pairs = zip(tests.slice(0, -1), tests.slice(1));

            const scoreDiffs = pairs.map(([mostRecentTest, secondMostRecentTest]) => {
                const mostRecentScore = getTestScore(mostRecentTest);
                const secondMostRecentScore = getTestScore(secondMostRecentTest);

                return mostRecentScore - secondMostRecentScore;
            });

            return mean(scoreDiffs);
        })
    );

    return {
        testTakenPercentage: Math.round((Object.keys(testHistoryByUser).length / totalStudents) * 100),
        averageTestCount: mean(Object.values(testHistoryByUser).map(tests => tests.length)).toFixed(1),
        averageScore: Math.round(averageScore * 100),
        averageTimeSpent: Math.round(averageTimeSpent),
        testScoreTrends: Math.round(averageIncreaseInScorePerRetest * 100),
    };
};

type UsersStudyAnalyticsPerFlashcard = Record<
    string,
    {
        progress: StudySessionProgressEnum;
        timeSpent: number;
    }
>;

export const getOverallFlashcardSetCompletion = ({
    studySessions,
}: {
    studySessions: StudySession[] | null | undefined;
}): Record<StudySessionProgressEnum, number> => {
    if (!studySessions) return null;

    if (!studySessions.length) {
        return DEFAULT_STUDY_PROGRESS;
    }

    return studySessions.reduce(
        (acc, curr) => {
            const completion = getProgressBucket(getProgressValue(curr.progress));
            acc[completion]++;
            return acc;
        },
        { ...DEFAULT_STUDY_PROGRESS }
    );
};

export type FlashcardsStudyAnalytics = Record<string, UsersStudyAnalyticsPerFlashcard>;

export const getFlashcardsStudyAnalytics = ({
    flashcardStudyStates,
    flashcardSet,
    userIds,
}: {
    flashcardStudyStates: FlashcardStudyState[] | null | undefined;
    flashcardSet: FlashcardSet | null | undefined;
    userIds?: string[];
}): FlashcardsStudyAnalytics => {
    const fillEmptyFlashcardIdsAndUsers = (retObj: FlashcardsStudyAnalytics) => {
        if (!flashcardSet || !flashcardSet.flashcards) return retObj;

        const DEFAULT_USER_STATS = {
            progress: StudySessionProgressEnum.NEW,
            timeSpent: 0,
        } as UsersStudyAnalyticsPerFlashcard[string];

        for (const flashcard of flashcardSet.flashcards) {
            // Fill empty flashcardIds (flashcards that haven't been started on by any user)
            if (!retObj[flashcard.flashcardId]) {
                retObj[flashcard.flashcardId] = {};
            }

            // Fill empty user stats (students that haven't started on a specific flashcard)
            if (Object.keys(retObj[flashcard.flashcardId]).length !== (userIds?.length || 0)) {
                for (const userId of userIds || []) {
                    if (!retObj[flashcard.flashcardId][userId]) {
                        retObj[flashcard.flashcardId][userId] = { ...DEFAULT_USER_STATS };
                    }
                }
            }
        }

        return retObj;
    };

    if (!flashcardStudyStates) return fillEmptyFlashcardIdsAndUsers({});

    const result: FlashcardsStudyAnalytics = {};

    for (const { flashcardId, userId, history, progress } of flashcardStudyStates?.filter(
        ({ userId }) => !userIds || userIds.includes(userId)
    ) ?? []) {
        result[flashcardId] ||= {} as UsersStudyAnalyticsPerFlashcard;
        result[flashcardId][userId] ||= {} as UsersStudyAnalyticsPerFlashcard[string];

        result[flashcardId][userId].timeSpent = history.reduce((acc, curr) => acc + curr.timeTaken, 0);
        result[flashcardId][userId].progress = progress;
    }

    return fillEmptyFlashcardIdsAndUsers(result);
};

export type FlashcardsMastery = Record<string, Record<StudySessionProgressEnum, number>>;

export const getFlashcardsMastery = ({
    flashcardsStudyAnalytics,
}: {
    flashcardsStudyAnalytics: FlashcardsStudyAnalytics;
}) => {
    return Object.entries(flashcardsStudyAnalytics).reduce((acc, [flashcardId, userStats]) => {
        const progress: StudySessionProgressEnum[] = Object.values(userStats).map(({ progress }) => progress);

        acc[flashcardId] = progress.reduce(
            (acc, curr) => {
                acc[curr]++;
                return acc;
            },
            { ...DEFAULT_STUDY_PROGRESS }
        );

        return acc;
    }, {} as FlashcardsMastery);
};

export const getOverallAssignmentCompletion = ({
    assignment,
    sessions,
    totalStudents,
}: {
    assignment: Assignment | null | undefined;
    sessions: AssignmentSessionGroupedByUser | null | undefined;
    totalStudents: number | undefined;
}) => {
    if (!assignment || !Object.keys(sessions || {})?.length) return DEFAULT_ASSIGNMENT_COMPLETION;

    const allCompletedSessions = Object.values(sessions || {}).flatMap(
        sessions => sessions?.filter(({ status }) => status === AssignmentStatus.COMPLETED) || []
    );

    const sortedCompletedSessionsByUser = fromEntries(
        Object.entries(sessions || {}).map(([userId, sessions]) => {
            return [
                userId,
                sessions
                    ?.filter(({ status }) => status === AssignmentStatus.COMPLETED)
                    ?.sort((a, b) => Number(b.submitted) - Number(a.submitted)) || [],
            ];
        })
    );

    const assignmentCompletionPercentage = allCompletedSessions.length / totalStudents;

    const getCountOfGrade = (grade: ResultGrade) => {
        return Object.values(sortedCompletedSessionsByUser).reduce((acc, curr) => {
            const mostRecentCompletedSession = curr[0];

            if (!mostRecentCompletedSession) return acc;

            const gradeFromScore = getGradeFromScore({ score: mostRecentCompletedSession.score, assignment });

            return acc + (gradeFromScore === grade ? 1 : 0);
        }, 0);
    };

    const mappedScores = Object.values(sortedCompletedSessionsByUser)
        .flatMap(sessions =>
            getGradeFromScore({
                score: sessions.find(({ status }) => status === AssignmentStatus.COMPLETED)?.score,
                assignment,
            })
        )
        .filter(Boolean);

    const averageGrade = mappedScores.length
        ? mean(mappedScores.map(score => getScoreFromGrade({ grade: score, assignment })))
        : null;

    const averageTimeSpent = mean(allCompletedSessions.map(({ duration }) => duration));

    return {
        assignmentCompletionPercentage: Math.round(assignmentCompletionPercentage * 100),
        countOfGradeA: getCountOfGrade(ResultGrade.A),
        countOfGradeB: getCountOfGrade(ResultGrade.B),
        countOfGradeC: getCountOfGrade(ResultGrade.C),
        countOfGradeD: getCountOfGrade(ResultGrade.D),
        countOfGradeF: getCountOfGrade(ResultGrade.F),
        averageGrade: averageGrade
            ? getGradeFromScore({
                  score: Math.round(averageGrade),
                  assignment,
              })
            : null,
        averageTimeSpent: Math.round(averageTimeSpent),
    };
};

export enum ResultGrade {
    A = "A",
    B = "B",
    C = "C",
    D = "D",
    F = "F",
}

export const getGradeFromScore = ({
    score,
    assignment,
}: {
    score: number | null | undefined;
    assignment: Assignment;
}): ResultGrade | null => {
    if (score === null || score === undefined) return null;

    if (score >= (assignment.settings.rubric?.A.score || DEFAULT_ASSIGNMENT_RUBRIC_INPUT.A.score)) return ResultGrade.A;
    if (score >= (assignment.settings.rubric?.B.score || DEFAULT_ASSIGNMENT_RUBRIC_INPUT.B.score)) return ResultGrade.B;
    if (score >= (assignment.settings.rubric?.C.score || DEFAULT_ASSIGNMENT_RUBRIC_INPUT.C.score)) return ResultGrade.C;
    if (score >= (assignment.settings.rubric?.D.score || DEFAULT_ASSIGNMENT_RUBRIC_INPUT.D.score)) return ResultGrade.D;

    return ResultGrade.F;
};

export const getScoreFromGrade = ({
    grade,
    assignment,
}: {
    grade: ResultGrade | null | undefined;
    assignment: Assignment;
}) => {
    if (!grade) return null;

    if (grade === ResultGrade.A) return assignment.settings.rubric?.A.score || DEFAULT_ASSIGNMENT_RUBRIC_INPUT.A.score;
    if (grade === ResultGrade.B) return assignment.settings.rubric?.B.score || DEFAULT_ASSIGNMENT_RUBRIC_INPUT.B.score;
    if (grade === ResultGrade.C) return assignment.settings.rubric?.C.score || DEFAULT_ASSIGNMENT_RUBRIC_INPUT.C.score;
    if (grade === ResultGrade.D) return assignment.settings.rubric?.D.score || DEFAULT_ASSIGNMENT_RUBRIC_INPUT.D.score;

    return 0;
};
